import { Controller } from "@hotwired/stimulus";
import Coloris from "@melloware/coloris";

export default class extends Controller {
  static values = {
    selector: String,
    swatches: { type: Array, default: [] },
  };

  connect() {
    if (!document.getElementById("clr-picker")) {
      Coloris.init();
      const picker = document.getElementById("clr-picker");
      if (picker) picker.setAttribute("data-turbo-permanent", "");
    }
    this.bind();
    document.addEventListener("turbo:morph", this.bind);
    // Catch fragments (Turbo Stream / frame replacements) that swap in fresh
    // colour inputs without disconnecting this controller — `bind` re-runs
    // Coloris and re-labels any unlabelled swatches.
    this.observer = new MutationObserver((mutations) => {
      const hasNewColorInput = mutations.some((m) =>
        [...m.addedNodes].some(
          (n) =>
            n.nodeType === Node.ELEMENT_NODE &&
            (n.matches?.(this.selectorValue) ||
              n.querySelector?.(this.selectorValue)),
        ),
      );
      if (hasNewColorInput) this.bind();
    });
    this.observer.observe(this.element, { childList: true, subtree: true });

    this.installToggleHandlers();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.bind);
    this.observer?.disconnect();
    this.uninstallToggleHandlers();
  }

  // Coloris closes the picker on document mousedown, then a click on the
  // same field re-opens it — so a second click on an open swatch flickers
  // closed-then-open instead of toggling off. Track which field is open
  // (via Coloris's open/close events on the input) and swallow the click
  // when mousedown happens on the already-open field. Capture phase so we
  // run before Coloris's bubble-phase click handler.
  installToggleHandlers() {
    this.openField = null;
    this.mousedownField = null;
    this.suppressNextClick = false;

    this.handleOpen = (e) => {
      if (e.target.matches?.(this.selectorValue)) {
        this.openField = e.target.closest(".inspector-color-field");
      }
    };
    this.handleClose = (e) => {
      if (e.target.matches?.(this.selectorValue)) this.openField = null;
    };
    this.handleMousedown = (e) => {
      this.mousedownField = e.target.closest(".inspector-color-field");
      this.suppressNextClick =
        this.openField !== null && this.mousedownField === this.openField;
    };
    this.handleClick = (e) => {
      if (
        this.suppressNextClick &&
        e.target.closest(".inspector-color-field") === this.mousedownField
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      this.mousedownField = null;
      this.suppressNextClick = false;
    };

    document.addEventListener("open", this.handleOpen, true);
    document.addEventListener("close", this.handleClose, true);
    document.addEventListener("mousedown", this.handleMousedown, true);
    document.addEventListener("click", this.handleClick, true);
  }

  uninstallToggleHandlers() {
    document.removeEventListener("open", this.handleOpen, true);
    document.removeEventListener("close", this.handleClose, true);
    document.removeEventListener("mousedown", this.handleMousedown, true);
    document.removeEventListener("click", this.handleClick, true);
  }

  bind = () => {
    Coloris({
      el: this.selectorValue,
      format: "hex",
      alpha: false,
      themeMode: "auto",
      formatToggle: false,
      swatches: this.swatchesValue,
    });
    this.labelSwatchButtons();
  };

  labelSwatchButtons() {
    document.querySelectorAll(this.selectorValue).forEach((input) => {
      const swatch = input.parentElement?.querySelector(":scope > button");
      if (!swatch || swatch.getAttribute("aria-label")) return;
      const label = input.id
        ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        : null;
      const labelText = label?.textContent?.trim();
      swatch.setAttribute(
        "aria-label",
        labelText ? `Choose ${labelText} color` : "Choose color",
      );
    });
  }
}
